import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import Icon from '../../ui/icons'
import { reply } from './replies'
import { seedQuery } from '../../data/seedQueries'
import { aiPrompts } from '../../data/seed'

/** Shared by the full AI Assistant page and the slide-over dock. */
export default function Chat({ compact = false, resetKey = 0 }) {
  const [messages, setMessages] = useState([])
  const [value, setValue] = useState('')
  const inputRef = useRef(null)
  const scrollRef = useRef(null)
  const timers = useRef([])

  const { data: candidates = [] } = useQuery(seedQuery('candidates'))
  const { data: recruiters = [] } = useQuery(seedQuery('recruiters'))

  useEffect(() => setMessages([]), [resetKey])

  useEffect(() => {
    const list = timers.current
    return () => list.forEach(clearTimeout)
  }, [])

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages])

  const send = useCallback(
    (text) => {
      const body = (text ?? value).trim()
      if (!body) return
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      setMessages((m) => [...m, { id: `u-${id}`, role: 'you', text: body }, { id: `a-${id}`, role: 'ai', typing: true }])
      setValue('')
      if (inputRef.current) inputRef.current.style.height = 'auto'

      // The prototype's artificial 850-1350ms latency, kept so the typing
      // indicator is visible rather than flashing.
      const t = setTimeout(() => {
        setMessages((m) =>
          m.map((msg) =>
            msg.id === `a-${id}`
              ? { ...msg, typing: false, node: reply(body, { candidates, recruiters }) }
              : msg,
          ),
        )
      }, 850 + Math.random() * 500)
      timers.current.push(t)
    },
    [value, candidates, recruiters],
  )

  const started = messages.length > 0

  return (
    <div className="chat-wrap" style={compact ? { height: '100%' } : undefined}>
      <div className="chat-scroll" ref={scrollRef}>
        {!started ? (
          <>
            <div className="ai-hero">
              <div className="ai-logo"><Icon name="sparkles" /></div>
              <h2 style={{ fontSize: compact ? 18 : 22, marginBottom: 6 }}>AI Recruiter Assistant</h2>
              <p className="text-muted">Ask anything about your candidates, jobs, and pipeline</p>
            </div>
            <div
              style={{
                display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center',
                maxWidth: 720, margin: '0 auto 10px',
              }}
            >
              {aiPrompts.slice(0, compact ? 6 : 12).map((p) => (
                <button key={p.text} className="prompt-chip" onClick={() => send(p.prompt)}>
                  <Icon name={p.icon} /> {p.text}
                </button>
              ))}
            </div>
          </>
        ) : (
          messages.map((m) => (
            <div className="chat-msg" key={m.id}>
              <div className={`chat-av ${m.role === 'you' ? 'user' : 'ai'}`}>
                <Icon name={m.role === 'you' ? 'users' : 'sparkles'} />
              </div>
              <div className="chat-bubble">
                <div className="chat-role">{m.role === 'you' ? 'You' : 'AI Assistant'}</div>
                {m.typing ? (
                  <div className="chat-typing"><span /><span /><span /></div>
                ) : (
                  <div className="chat-text">{m.text ?? m.node}</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div style={{ paddingTop: 12 }}>
        <div className="chat-input-bar">
          <textarea
            ref={inputRef}
            rows={1}
            value={value}
            placeholder="Message AI Assistant…"
            onChange={(e) => {
              setValue(e.target.value)
              e.target.style.height = 'auto'
              e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                send()
              }
            }}
          />
          <button className="chat-send" onClick={() => send()} aria-label="Send">
            <Icon name="arrow-right" />
          </button>
        </div>
        <p className="text-muted text-sm" style={{ textAlign: 'center', marginTop: 8 }}>
          UI preview · responses are simulated. <Icon name="lock" /> API-ready for backend integration.
        </p>
      </div>
    </div>
  )
}
